import React, { useContext, useEffect, useState } from 'react'

import Box from '@components/kit/box'
import Text from '@components/kit/text'
import Modal from '@components/kit/modal'
import Loading from '@components/kit/loading'

import { useChatStore } from '@store/Chat'
import { useAuthStore } from '@store/Auth'
import { ChatSuggestion } from '@store/Chat/types'
import PostItCard from '@components/postItCard'
import { FormButtonPanel, TextFieldFormated } from '@components/formFields'
import { ChatContext } from '../../chat.web'
import Pressable from '@components/kit/pressable'
import Button from '@components/kit/button'
import { Form, Formik } from 'formik'

interface SuggestionCardProps {
  suggestion: ChatSuggestion
  onSend: (suggestionId: number, text: string, isEdited: boolean) => void
  onReject: (suggestionId: number) => void
  onEdit: (suggestion: ChatSuggestion) => void
}

function SuggestionCard({
  suggestion,
  onSend,
  onReject,
  onEdit
}: SuggestionCardProps) {
  const [editText, setEditText] = useState(suggestion.text)
  const [isEditing, setIsEditing] = useState(false)

  const handleConfirmEditing = () => {
    setIsEditing(false)
    suggestion.text = editText
    onEdit(suggestion)
  }

  const handleConfirmAndSendEditing = () => {
    handleConfirmEditing()
    onSend(suggestion.suggestion_id, editText, true)
  }

  const handleCancelEditing = () => {
    setIsEditing(false)
    setEditText(suggestion.text)
  }

  return (
    <PostItCard variant='default'>
      {isEditing ? (
        <Box>
          <TextFieldFormated
            name='suggestionText'
            label='Edit Suggestion'
            value={editText}
            onChangeText={text => setEditText(text)}
            placeholder='Type your message here...'
            multiline={true}
            autoFocus={true}
          />
        </Box>
      ) : (
        <Pressable
          flexDirection='row'
          justifyContent='flex-start'
          onPress={() => setIsEditing(true)}
          onPressIn={() => setIsEditing(true)}
        >
          <Text font='text.14.bold' textColor='walrusBlue200' mr='08'>
            Message:
          </Text>
          <Text font='text.14.medium' textColor='walrusBlue200'>
            {suggestion.text}
          </Text>
        </Pressable>
      )}
      <Box
        flex='1 1 auto'
        width='100%'
        flexDirection='row'
        alignItems='flex-start'
        justifyContent='space-between'
      >
        <Box flexDirection='row' marginRight='08' paddingTop='08'>
          {!isEditing && (
            <Button
              variant='tertiary'
              size='small'
              onPress={() => setIsEditing(true)}
            >
              Edit
            </Button>
          )}
        </Box>

        <Box>
          <FormButtonPanel
            onCancel={() =>
              isEditing
                ? handleCancelEditing()
                : onReject(suggestion.suggestion_id)
            }
            onSave={() =>
              isEditing
                ? handleConfirmAndSendEditing()
                : onSend(suggestion.suggestion_id, suggestion.text, false)
            }
            cancelLabel={isEditing ? 'Cancel' : 'Reject'}
            saveLabel={'Send'}
            saveVariant='paleGreen'
            cancelVariant='paleRed'
            saveSize='small'
            cancelSize='small'
            paddingTop='08'
            gap='08'
          />
        </Box>
      </Box>
    </PostItCard>
  )
}

export function ChatSuggestions() {
  const { userId, roomId } = useContext(ChatContext)
  const professionalId = useAuthStore(state => state.userId)

  const {
    getChatSuggestionsByUserId,
    sendChatSuggestion,
    rejectChatSuggestion,
    sendTextMessage
  } = useChatStore.getState()
  const [chatSuggestions, setChatSuggestions] = useState<ChatSuggestion[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const submitMessage = (text: string) => {
    if (typeof text !== 'string' || !text.trim()) {
      return false
    }
    sendTextMessage(userId, text, {
      roomId: roomId ?? 0,
      ownerId: professionalId ?? 0
    })
    return true
  }

  const handleEditMessage = (suggestion: ChatSuggestion) => {
    const updatedSuggestions = chatSuggestions.map(s => {
      if (s.suggestion_id === suggestion.suggestion_id) {
        s.text = suggestion.text
      }
      return s
    })
    setChatSuggestions(updatedSuggestions)
  }

  const handleSendSuggestion = async (
    suggestionId: number,
    text: string,
    isEdited: boolean
  ) => {
    if (isSubmitting) {
      return
    }
    setIsSubmitting(true)
    let success = false
    success = submitMessage(text)
    if (success) {
      if (isEdited) {
        success = await sendChatSuggestion(userId, suggestionId, text)
      } else {
        success = await sendChatSuggestion(userId, suggestionId)
      }
    }
    if (success) {
      setIsLoading(true)
    } else {
      setHasError(true)
      setTimeout(() => {
        setHasError(false)
      }, 2900)
    }
    setIsSubmitting(false)
  }

  const handleRejectSuggestion = async (suggestionId: number) => {
    if (isSubmitting) {
      return
    }
    setIsSubmitting(true)
    const success = await rejectChatSuggestion(userId, suggestionId)
    if (success) {
      setIsLoading(true)
    } else {
      setHasError(true)
      setTimeout(() => {
        setHasError(false)
      }, 2900)
    }
    setIsSubmitting(false)
  }

  useEffect(() => {
    if (isLoading && userId) {
      getChatSuggestionsByUserId(userId).then(suggestions => {
        if (suggestions) {
          suggestions.sort((a, b) => a.order_number - b.order_number)
          setChatSuggestions(suggestions)
          setIsLoading(false)
        }
      })
    }
  }, [userId, getChatSuggestionsByUserId, isLoading])

  if (!chatSuggestions?.length && !isLoading) {
    return null
  }

  return (
    <Formik initialValues={{}} onSubmit={() => {}} validateOnChange={true}>
      <Form>
        <Box mb='16'>
          <Text ml='12' font='display.16.bold'>
            Chat Suggestions
          </Text>
          <Box m='12'>
            {chatSuggestions.map(suggestion => (
              <Box key={suggestion.suggestion_id} mb='16'>
                <SuggestionCard
                  suggestion={suggestion}
                  onSend={handleSendSuggestion}
                  onReject={handleRejectSuggestion}
                  onEdit={handleEditMessage}
                />
              </Box>
            ))}
            {!chatSuggestions?.length && !isLoading && (
              <Text font='text.14.medium' textColor='walrusBlue700'>
                No chat suggestions available.
              </Text>
            )}
            {(isLoading || isSubmitting) && (
              <Box
                position='absolute'
                width='100%'
                height='100%'
                top='0'
                left='0'
                right='0'
                flex='1'
                justifyContent='center'
                alignItems='center'
                style={{
                  backgroundColor: '#f4f7facc'
                }}
              >
                <Loading size='large' />
              </Box>
            )}
          </Box>
          <Modal
            open={hasError}
            onRequestClose={() => setHasError(false)}
            showCloseButton={true}
            borderRadius='medium'
            backgroundColor='walrusRed050'
          >
            <Box p='24'>
              <Text font='display.20.bold' textColor='error'>
                Error on Chat Suggestion Request
              </Text>
              <Text mt='12' font='text.16.medium' textColor='walrusRed800'>
                An error occurred while processing your request. Please try
                again.
              </Text>
            </Box>
          </Modal>
        </Box>
      </Form>
    </Formik>
  )
}
